import { z } from "zod";
import { codeToolSecretNameSchema } from "../../helpers/codeToolTurn.js";
import { callDirectionSchema } from "../callDirection.js";
import { ianaTimeZoneSchema } from "../company.js";
import { contactPublicApiSchema } from "../contactPublicApi.js";
import {
  baseToolPatchPayloadSchema,
  baseToolPostPayloadSchema
} from "./base.js";
import { customToolMockSchema, customToolParameterSchema } from "./custom.js";
import { webDocumentResearchToolAllowedDomainSchema } from "./webDocumentResearch.js";
const CodeToolType = "code";
const defaultCodeToolTimeoutMs = 1e4;
const codeToolMaxCodeLength = 1e5;
const codeToolMaxTimeoutMs = 3e4;
const codeToolMaxAllowedDomainCount = 20;
const codeToolMaxSecretCount = 20;
const codeToolAllowedDomainListSchema = z.array(webDocumentResearchToolAllowedDomainSchema).max(
  codeToolMaxAllowedDomainCount,
  `Add no more than ${codeToolMaxAllowedDomainCount.toString()} allowed domains.`
);
function dedupeDomains(domains) {
  return Array.from(new Set(domains));
}
const codeToolSettingsSchema = z.object({
  code: z.string().nonempty().max(codeToolMaxCodeLength),
  parameters: z.array(customToolParameterSchema).optional(),
  /** The hostnames the script may send requests to, matched exactly. */
  allowed_domains: codeToolAllowedDomainListSchema.transform(dedupeDomains),
  timeout_ms: z.int().positive().max(codeToolMaxTimeoutMs).optional(),
  /**
   * Mock configuration for simulations.
   * If not provided, a default simulation message will be used.
   */
  mock: customToolMockSchema.optional()
});
const codeToolSecretWriteSchema = z.object({
  value: z.string().min(4).max(4096),
  allowed_domains: codeToolAllowedDomainListSchema.min(1, "Add at least one domain the secret may be sent to.").transform(dedupeDomains)
});
const codeToolSecretsWriteSchema = z.record(codeToolSecretNameSchema, codeToolSecretWriteSchema.nullable()).refine((secrets) => Object.keys(secrets).length <= codeToolMaxSecretCount, {
  message: `A tool can have no more than ${codeToolMaxSecretCount.toString()} secrets.`
});
function describeCodeToolSecretDomainsOutsideTool(secrets, toolAllowedDomains) {
  const toolDomains = new Set(toolAllowedDomains);
  const problems = [];
  for (const [name, secret] of Object.entries(secrets)) {
    const outside = (secret?.allowed_domains ?? []).filter(
      (domain) => !toolDomains.has(domain)
    );
    if (outside.length > 0) {
      problems.push(`${name} (${outside.join(", ")})`);
    }
  }
  return problems.length === 0 ? void 0 : `Secrets can only be sent to the tool's allowed domains: ${problems.join("; ")}.`;
}
const codeToolInputCustomFieldsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()])
);
const codeToolInputContactSchema = contactPublicApiSchema.extend({
  // Null for a simulated call's contact, which exists only in its setup.
  id: z.string().nullable(),
  custom_fields: codeToolInputCustomFieldsSchema
});
const codeToolInputCallSchema = z.object({
  direction: callDirectionSchema.nullable(),
  from: z.string().nullable(),
  to: z.string().nullable(),
  ext_id: z.string().nullable()
});
const codeToolCallFactsSchema = z.object({
  customer_interaction: z.object({
    id: z.string().nullable(),
    custom_fields: codeToolInputCustomFieldsSchema
  }).nullable(),
  contact: codeToolInputContactSchema.nullable(),
  call: codeToolInputCallSchema,
  company_timezone: ianaTimeZoneSchema
});
function buildEmptyCodeToolCallFacts(companyTimezone) {
  return {
    customer_interaction: null,
    contact: null,
    call: { direction: null, from: null, to: null, ext_id: null },
    company_timezone: companyTimezone
  };
}
function checkCodeToolSettings(settings, ctx) {
  const seenNames = /* @__PURE__ */ new Set();
  for (const { name } of settings.parameters ?? []) {
    if (seenNames.has(name)) {
      ctx.addIssue({
        code: "custom",
        message: `"${name}" is used by more than one parameter.`,
        path: ["settings", "parameters"]
      });
      return;
    }
    seenNames.add(name);
  }
}
function checkCodeToolSecretDomains(secrets, settings, ctx) {
  if (!secrets) return;
  const message = describeCodeToolSecretDomainsOutsideTool(
    secrets,
    settings.allowed_domains
  );
  if (message) {
    ctx.addIssue({ code: "custom", message, path: ["secrets"] });
  }
}
const codeToolPostPayloadSchema = baseToolPostPayloadSchema.extend({
  type: z.literal(CodeToolType),
  settings: codeToolSettingsSchema,
  secrets: codeToolSecretsWriteSchema.optional()
}).superRefine((payload, ctx) => {
  checkCodeToolSettings(payload.settings, ctx);
  checkCodeToolSecretDomains(payload.secrets, payload.settings, ctx);
});
const codeToolPatchPayloadSchema = baseToolPatchPayloadSchema.extend({
  type: z.literal(CodeToolType),
  settings: codeToolSettingsSchema.optional(),
  secrets: codeToolSecretsWriteSchema.optional()
}).superRefine((payload, ctx) => {
  if (payload.settings) {
    checkCodeToolSettings(payload.settings, ctx);
    checkCodeToolSecretDomains(payload.secrets, payload.settings, ctx);
  }
});
export {
  CodeToolType,
  buildEmptyCodeToolCallFacts,
  codeToolCallFactsSchema,
  codeToolMaxAllowedDomainCount,
  codeToolMaxCodeLength,
  codeToolMaxSecretCount,
  codeToolMaxTimeoutMs,
  codeToolPatchPayloadSchema,
  codeToolPostPayloadSchema,
  codeToolSecretsWriteSchema,
  codeToolSettingsSchema,
  defaultCodeToolTimeoutMs,
  describeCodeToolSecretDomainsOutsideTool
};
//# sourceMappingURL=code.js.map
